// src/render/renderFirstSection.ts
/**
 * renderFirstSection.ts (BASIC STORIES ONLY)
 * ------------------------------------------
 * Renders the very first (basic) section of the story, applies narration gate if present,
 * sets HUD type, primes reveals, ignites background, and initializes observer flow.
 */
import { log } from "../utils/debug";
import { renderSection } from "./sectionBuilder";
import { renderImageBackground } from "./renderImageBackground";
import { primeSectionForReveal } from "../observer/reveal";
import { forceBackgroundIgnition } from "../observer/bgIgnition";
import { initHiddenState } from "../utils/normalize";
import type { SectionData } from "../types";
import { showAudioGate } from "../elements/audioGate";
import { observeSections } from "../observer/observerSections";
import { setAudioAllowed, enforceBackgroundAudioMix } from "../utils/mediaUtils";
import { AudioController } from "../utils/audioController";
import { setHudType } from "../utils/hudUtils";
import { refreshHudState } from "../utils/hudState";
import { addSectionArrows, revealArrowsForSection, initArrowNavigation } from "../elements/arrow";
import { Z } from "../utils/constants";
import { attachLiteHUD, type HudHandle } from "../elements/hudLite";
import { MediaUnlock } from "../utils/mediaUnlock";
import { animateElements } from "../elements/animate";
import { playAudioOnce } from "../observer/audioOnce";

import { scanAndRenderInlineImages } from "./imageInline";
import { renderHeaderImages } from "./imageOverlay"; // ✅ refactor: unified overlay renderer

// --- helpers: make overlay wrappers visible after primer (wrappers are non-anim nodes)
function ensureOverlayHostsVisible(section: HTMLElement): void {
  section
    .querySelectorAll<HTMLElement>('[data-role="image-overlay-container"] .section-image')
    .forEach((host) => {
      host.dataset.animSkip = "1"; // guard against primer
      host.dataset.animated = "true"; // neutralize CSS that hides [data-animated="false"]
      host.style.visibility = "visible";
      host.style.opacity = "1";
    });
}

// --- helpers: ensure overlay <img> are real anim targets
function ensureOverlayImgsTargetable(section: HTMLElement): void {
  section
    .querySelectorAll<HTMLImageElement>('[data-role="image-overlay-container"] img')
    .forEach((img) => {
      img.classList.add("sc-anim");
      if (!img.hasAttribute("data-anim")) img.setAttribute("data-anim", "fade-in");
      // if primer set them hidden, let animator flip them back:
      if (!img.dataset.animated) img.dataset.animated = "false";
    });
}

// Idempotent prime for hidden/anim state on a section
function ensureHiddenPrimed(section: HTMLElement) {
  if (section?.dataset?.animInit === "1") return;
  initHiddenState(section);
  section.dataset.animInit = "1";
}

/** Ensure one background layer exists for section-0 and seed it fresh. */
function seedFirstBackground(section: HTMLElement, attrs: Record<string, any>, baseUrl: string) {
  const layers = Array.from(section.querySelectorAll(".bg-image-layer")) as HTMLElement[];
  layers.forEach((el) => { try { el.remove(); } catch {} });
  renderImageBackground(section, attrs, baseUrl);
}

function markSectionVisible(section: HTMLElement) {
  section.classList.add("visible");
  if (section.id === "section-0") {
    const ensureVisible = () => {
      if (!section.classList.contains("visible")) {
        section.classList.add("visible");
        log("[RENDER-First] Re-added .visible to section-0");
      }
    };
    setTimeout(ensureVisible, 1000);
    setTimeout(ensureVisible, 2000);
  }
}

function dismissGate(gate: HTMLElement) {
  gate.classList.add("fade-out");
  let done = false;
  const cleanup = () => {
    if (done) return;
    done = true;
    try { gate.remove(); } catch {}
  };
  const once = { once: true } as const;
  gate.addEventListener("transitionend", cleanup, once);
  gate.addEventListener("animationend", cleanup, once);
  setTimeout(cleanup, 1000);
}


export async function renderRemainingSections(
  baseUrl: string,
  sections: SectionData[],
  startIndex = 1
): Promise<void> {
  const wrapper = document.getElementById("scroll-wrapper");
  if (!wrapper) return;
  const tail = sections.slice(startIndex);
  tail.forEach((section, i) => {
    const idx = startIndex + i;
    if (document.getElementById(`section-${idx}`)) return;

    const el = renderSection(section.attrs, section.content, false, baseUrl);
    el.id = `section-${idx}`;
    el.dataset.sectionIndex = `${idx}`;
    el.style.minHeight = "100vh";
    el.style.scrollSnapAlign = "start";
    el.style.scrollSnapStop = "always";

    // 1) Overlay images (front-matter) for tail section
    try { renderHeaderImages(el, section.attrs as any, 0); }
    catch (e) { console.warn("[renderRemainingSections] overlay pass failed for", `section-${idx}`, e); }

    // 2) Inline @@ images within this section
    try { scanAndRenderInlineImages(el, 0); }
    catch (e) { console.warn("[renderRemainingSections] inline image pass failed for", `section-${idx}`, e); }

    // 3) Prime hidden state AFTER seeding images
    ensureHiddenPrimed(el);

    // 3.1) Fix overlay wrapper visibility post-prime (critical for overlays)
    ensureOverlayHostsVisible(el);
    ensureOverlayImgsTargetable(el);

    // 4) Mount
    wrapper.appendChild(el);
    try { MediaUnlock.primeExistingVideos(`#section-${idx} video`, { autoPlay: false }); } catch {}
  });
  observeSections();
}

function applyFirstSectionAudioDecision(
  firstEl: HTMLElement,
  attrs: SectionData["attrs"] | undefined,
  optedIn: boolean
): void {
  const narr = firstEl.querySelector<HTMLAudioElement>(
    'audio[data-role="narration"], audio[data-narration], audio.narration, audio[data-section-audio="true"]'
  );

  // Update global audio state and gate flags
  AudioController.setMuted(!optedIn, "hud");
  setAudioAllowed(optedIn);

  if (optedIn) {
    if (narr) {
      const loop =
        attrs?.loop === true ||
        attrs?.loop === "true";

      narr.loop = loop;
      narr.muted = false;
      try {
        // Avoid resetting if playback already began (prevents audible restart on gate unmute).
        if (!loop && narr.paused) narr.currentTime = 0;
      } catch {}

      try {
        setTimeout(() => {
          try { void playAudioOnce(narr); } catch {}
        }, 500);
      } catch {}
    }

    try {
      enforceBackgroundAudioMix(firstEl);
    } catch {}

    firstEl.dataset.audioStarted = "1";
  } else if (narr) {
    try {
      narr.pause();
    } catch {}
    try {
      narr.currentTime = 0;
    } catch {}
    narr.muted = true;
  }
}

export async function renderFirstSection(baseUrl: string, sections: SectionData[]): Promise<void> {
  log("[RENDER-First] (basic) called with", sections.length, "sections");
  if (sections.length < 1) return;

  const [first] = sections;
  const wrapper = document.getElementById("scroll-wrapper")!;
  if (!wrapper) return;

  // Always arm the one-time gesture unlock so reels/media can gain audio in non-narrated stories.
  try { MediaUnlock.attachFirstGestureUnlock(); } catch {}

  const firstEl = document.getElementById("section-0") as HTMLElement | null;
  if (!firstEl) {
    log("[RENDER-First] Missing #section-0. Core should create and append the first section element before calling renderFirstSection.");
    return;
  }

  initArrowNavigation();

  document.documentElement.classList.remove("gate-hold");
  document.body.classList.remove("gate-hold");
  firstEl.classList.remove("gate-hold");

  setHudType(firstEl, first.attrs);
  seedFirstBackground(firstEl, first.attrs, baseUrl);

  // Hydrate textual/content DOM into section-0 (keep seeded bg layer)
  try {
    const tmp = renderSection(first.attrs, first.content, true, baseUrl);
    Array.from(tmp.children).forEach((child) => {
      const el = child as HTMLElement;
      // Skip BG layer and overlay hosts; they will be (re)built elsewhere
      if (
        el.classList?.contains("bg-image-layer") ||
        el.matches?.('[data-role="image-overlay-container"], [data-role="image-overlay-interactive"]')
      ) return;
      firstEl.appendChild(el);
    });
  } catch (e) {
    log("[RENDER-First] Failed to hydrate content into section-0", e);
  }

  // Render overlay + inline images for section-0 (manual builder path)
  try {
    renderHeaderImages(firstEl, first.attrs as any, 0);
    scanAndRenderInlineImages(firstEl, 0);
  } catch (e) {
    log("[RENDER-First] image passes failed in section-0", e);
  }

  // Prime AFTER seeding images so all [data-anim] start hidden.
  try { ensureHiddenPrimed(firstEl); }
  catch (e) { log("[RENDER-First] Early initHiddenState failed (pre-gate)", e); }

  // Fix overlay wrapper visibility post-prime (critical for overlays)
  ensureOverlayHostsVisible(firstEl);
  ensureOverlayImgsTargetable(firstEl);

  const hasNarration = Boolean(first?.attrs?.audio);
  log("[RENDER-First] hasNarration?", { hasNarration, audioAttr: first?.attrs?.audio });

  // Narration stories already armed above; keep hook for readability.
  if (hasNarration) {
    MediaUnlock.attachFirstGestureUnlock();
  }

  // HUD mount (hidden until after gate if narration)
  const hudHandle = attachLiteHUD(firstEl) as HudHandle | null;
  const hudControls = (hudHandle?.root ?? (firstEl.querySelector(".lite-hud-controls") as HTMLElement | null));
  if (hudControls) {
    if (hasNarration) {
      hudControls.style.opacity = "0";
      hudControls.style.visibility = "hidden";
      hudControls.style.pointerEvents = "none";
      try {
        const z = getComputedStyle(hudControls).zIndex;
        const vis = getComputedStyle(hudControls).visibility;
        const op = getComputedStyle(hudControls).opacity;
        log("[HUD] mounted + hidden", { z, vis, op, rect: hudControls.getBoundingClientRect() });
      } catch {}
    } else {
      try { refreshHudState(firstEl); } catch {}
      hudControls.style.opacity = "1";
      hudControls.style.visibility = "visible";
      hudControls.style.pointerEvents = "auto";
    }
  }

  // Clear legacy CSS vars on bg anim sprite
  {
    const spr = firstEl.querySelector<HTMLElement>(".bg-anim-sprite");
    try {
      spr?.style.removeProperty("--slide-from-x");
      spr?.style.removeProperty("--slide-from-y");
    } catch {}
  }

  // Prep narration/media elements (no creation)
  const narr = firstEl.querySelector<HTMLAudioElement>(
    'audio[data-role="narration"], audio[data-narration], audio.narration, audio[data-section-audio="true"]'
  );
  if (narr) {
    narr.muted = true;
    try { narr.pause(); } catch {}
    try { if (!narr.loop) narr.currentTime = 0; } catch {}
  }

  const firstVideo = firstEl.querySelector<HTMLVideoElement>("video.sc-media, video");
  if (firstVideo) {
    firstVideo.muted = true;
    firstVideo.playsInline = true;
    firstVideo.setAttribute("muted", "");
    firstVideo.setAttribute("playsinline", "");
    firstVideo.setAttribute("webkit-playsinline", "");
    if (!firstVideo.preload) firstVideo.preload = "metadata";
    firstVideo.addEventListener("canplay", () => {
      try { if (firstVideo.currentTime === 0) firstVideo.currentTime = 0.001; } catch {}
    }, { once: true });
    try { firstVideo.pause(); } catch {}
  }

  MediaUnlock.primeExistingVideos(
    "section#section-0 video.bg-video, section#section-0 video[data-bg], section#section-0 video.sc-media, section#section-0 video"
  );

  let gateEl: HTMLElement | null = null;

  const continueAfterGate = async (optedIn: boolean) => {
    if (gateEl) {
      try { dismissGate(gateEl); } catch {}
      gateEl = null;
    }

    log("[RENDER-First] Gate decision:", { optedIn });

    document.documentElement.classList.remove("gate-hold");
    document.body.classList.remove("gate-hold");
    firstEl.classList.remove("gate-hold");

    try { await MediaUnlock.unlockNow("gate"); } catch {}

    applyFirstSectionAudioDecision(firstEl, first.attrs as any, optedIn);

    (window as any).__storycoreGateGraceUntil = Date.now() + 1500;

    let tailStarted = false;
    const startTail = () => {
      if (tailStarted) return;
      tailStarted = true;
      try {
        log("[RENDER-First] Starting renderRemainingSections tail…");
        renderRemainingSections(baseUrl, sections, 1);
      } catch (e) {
        log("[RENDER-First] Failed to start tail rendering:", e);
      }
    };

    const doFirstPass = async () => {
      try {
        ensureHiddenPrimed(firstEl);
        // ⬇️ re-assert overlay visibility after primer
        ensureOverlayHostsVisible(firstEl);
        ensureOverlayImgsTargetable(firstEl);

        primeSectionForReveal(firstEl);
        markSectionVisible(firstEl);
        firstEl.dataset.allowFirstReveal = "1";
        try { refreshHudState(firstEl); } catch {}

        try {
          const controls = firstEl.querySelector(".lite-hud-controls") as HTMLElement | null;
          if (controls) {
            controls.style.opacity = "1";
            controls.style.pointerEvents = "auto";
            controls.style.visibility = "visible";
          }
        } catch {}

        try {
          animateElements(firstEl).then(() => {
            if (first.attrs?.arrow) {
              addSectionArrows(firstEl, first.attrs.arrow, Z.HUD);
              revealArrowsForSection(firstEl);
            }
          });
        } catch (err) {
          log("[RENDER-First] animateElements failed:", err);
        }

        try {
          forceBackgroundIgnition(firstEl);
          firstEl.dataset.bgIgnited = "1";
        } catch (err) {
          log("[RENDER-First] force ignition failed", err);
        }

        try {
          const _n = firstEl.querySelector<HTMLAudioElement>('audio[data-role="narration"], audio[data-narration], audio.narration, audio[data-section-audio="true"]');
          if (optedIn && _n && _n.paused) { await _n.play().catch(() => {}); }
        } catch {}
      } finally {
        startTail();
      }
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(() => { void doFirstPass(); });
    });
  };

  if (hasNarration) {
    document.documentElement.classList.add("gate-hold");
    document.body.classList.add("gate-hold");
    firstEl.classList.add("gate-hold");

    try {
      gateEl = showAudioGate(first.attrs, first.content, continueAfterGate);
      if (!gateEl.isConnected) (document.body || document.documentElement).appendChild(gateEl);
    } catch (err) {
      log("[RENDER-First] Error showing gate:", err);
      await continueAfterGate(false);
    }
    return;
  }

  // No narration gate path
  firstEl.dataset.allowFirstReveal = "1";
  try {
    ensureHiddenPrimed(firstEl);
    // ⬇️ re-assert overlay visibility after primer
    ensureOverlayHostsVisible(firstEl);
    ensureOverlayImgsTargetable(firstEl);

    primeSectionForReveal(firstEl);
    markSectionVisible(firstEl);
    try { refreshHudState(firstEl); } catch {}

    const controls = firstEl.querySelector(".lite-hud-controls") as HTMLElement | null;
    if (controls) {
      controls.style.opacity = "1";
      controls.style.pointerEvents = "auto";
      controls.style.visibility = "visible";
    }

    try {
      animateElements(firstEl).then(() => {
        if (first.attrs?.arrow) {
          addSectionArrows(firstEl, first.attrs.arrow, Z.HUD);
          revealArrowsForSection(firstEl);
        }
      });
    } catch (e) {
      log("[RENDER-First] animateElements (no-audio immediate) failed:", e);
    }

  try { forceBackgroundIgnition(firstEl); firstEl.dataset.bgIgnited = "1"; } catch (e) {
    log("[RENDER-First] forceBackgroundIgnition (no-audio immediate) failed:", e);
  }
  try { enforceBackgroundAudioMix(firstEl); } catch {}
  } catch (e) {
    log("[RENDER-First] Immediate reveal pipeline (no-audio) encountered an issue:", e);
  }

  try { await renderRemainingSections(baseUrl, sections, 1); } catch {}

  try {
    const anims = Array.from(firstEl.querySelectorAll(".sc-anim")) as HTMLElement[];
    if (anims.length && anims.every(n => {
      const cs = getComputedStyle(n);
      return cs.visibility === "hidden" || cs.opacity === "0";
    })) {
      animateElements(firstEl);
    }
  } catch {}

  try {
    const s0 = document.getElementById("section-0") as HTMLElement | null;
    if (s0 && !s0.dataset.allowFirstReveal) {
      ensureHiddenPrimed(s0);
      // ⬇️ re-assert overlay visibility after primer
      ensureOverlayHostsVisible(s0);
      ensureOverlayImgsTargetable(s0);

      primeSectionForReveal(s0);
      markSectionVisible(s0);
      s0.dataset.allowFirstReveal = "1";
      try { refreshHudState(s0); } catch {}
      const controls = s0.querySelector(".lite-hud-controls") as HTMLElement | null;
      if (controls) {
        controls.style.opacity = "1";
        controls.style.pointerEvents = "auto";
        controls.style.visibility = "visible";
      }
      try { animateElements(s0); } catch {}
      try { forceBackgroundIgnition(s0); s0.dataset.bgIgnited = "1"; } catch {}
      log("[RENDER-First] Safety kick applied: revealed + ignited section-0");
    }
  } catch {}

  try {
    setTimeout(() => {
      try {
        const wrapperNow = document.getElementById("scroll-wrapper");
        const expected = sections.length;
        const current = wrapperNow ? wrapperNow.children.length : 0;
        if (!wrapperNow || current < Math.min(2, expected)) {
          log("[RENDER-First] Safety kick: starting tail render…", { current, expected });
          void renderRemainingSections(baseUrl, sections, 1);
        }
      } catch {}
    }, 0);
  } catch {}
}
